// ─────────────────────────────────────────────────────────────────────────────
// Cinematic page kit — the building blocks Home uses, shared by every public
// page: android/prop slots for the fx3d stage, section labels, links, pinned
// scroll steps, and CineHero (the 3-D android hero that replaced the video
// heroes; a page's original film is kept behind a "Watch the film" button).
// Colours come from the theme tokens (tailwind `cx`), so light/dark just works.
// ─────────────────────────────────────────────────────────────────────────────
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import { AnimatePresence, motion, useMotionValueEvent, useScroll, useTransform, type MotionValue } from "framer-motion";
import { ArrowRight, Play, X } from "lucide-react";
import { Magnetic } from "@/components/fx3d/HeroFx";
import { fx } from "@/components/fx3d/fxStore";
import { buildYouTubeEmbedUrl, extractYouTubeId } from "@/lib/youtube";

export const easeOut = [0.16, 1, 0.3, 1] as const;

export type Who = "male" | "female";
export interface CastMember {
  who: Who;
  pose: string;
  face?: number;
}

export const ActorSlot = ({
  who,
  pose,
  face = 0,
  scrub,
  className,
}: CastMember & { scrub?: "turn"; className: string }) => (
  <div
    aria-hidden
    data-actor={who}
    data-pose={pose}
    data-face={face}
    data-scrub={scrub}
    className={`pointer-events-none absolute ${className}`}
  />
);

export const PropSlot = ({ name, step = 0, className }: { name: string; step?: number; className: string }) => (
  <div aria-hidden data-prop={name} data-step={step} className={`pointer-events-none absolute ${className}`} />
);

/** Small mono section label (no numbering). */
export const Label = ({ children, className = "" }: { children: React.ReactNode; className?: string }) => (
  <p className={`font-hud text-[11px] uppercase tracking-[0.24em] text-cx-link ${className}`}>{children}</p>
);

export const AccentLink = ({ to, children, external }: { to: string; children: React.ReactNode; external?: boolean }) => {
  const cls = "group inline-flex items-center gap-2 text-sm font-medium text-cx-link hover:text-cx-fg transition-colors";
  const inner = (
    <>
      {children}
      <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
    </>
  );
  if (external) return <a href={to} target="_blank" rel="noopener noreferrer" className={cls}>{inner}</a>;
  if (to.startsWith("#")) return <a href={to} className={cls}>{inner}</a>;
  return <Link to={to} className={cls}>{inner}</Link>;
};

/** Sticky, multi-step act: the section is `steps` screens tall. */
export function usePinnedStep(ref: React.RefObject<HTMLElement>, steps: number) {
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end end"] });
  const [step, setStep] = useState(0);
  useMotionValueEvent(scrollYProgress, "change", (p) => {
    const n = Math.min(steps - 1, Math.max(0, Math.floor(p * steps)));
    if (n !== step) {
      setStep(n);
      fx.typingAt = performance.now(); // eye/seam flare on every beat
    }
  });
  return { step, progress: scrollYProgress };
}

export const StepRail = ({ step, labels }: { step: number; labels: string[] }) => (
  <ol className="flex gap-2">
    {labels.map((label, i) => (
      <li key={label} className="flex-1">
        <div className="h-px w-full overflow-hidden bg-cx-fg/15">
          <motion.div
            className="h-full origin-left bg-[#2A629E]"
            animate={{ scaleX: i <= step ? 1 : 0 }}
            transition={{ duration: 0.6, ease: easeOut }}
          />
        </div>
        <span className={`mt-2 hidden font-hud text-[10px] uppercase tracking-[0.18em] sm:block ${i === step ? "text-cx-fg" : "text-cx-fg/35"}`}>
          {label}
        </span>
      </li>
    ))}
  </ol>
);

/** Giant faint word drifting behind a section. */
export const Ghost = ({ children, progress }: { children: React.ReactNode; progress?: MotionValue<number> }) => {
  const fallback = useScroll().scrollYProgress;
  const x = useTransform(progress ?? fallback, [0, 1], ["4%", "-12%"]);
  return (
    <motion.div
      aria-hidden
      style={{ x }}
      className="pointer-events-none absolute inset-x-0 top-[38%] select-none whitespace-nowrap font-cine text-[24vw] font-bold leading-none tracking-[-0.06em] text-cx-fg/[0.035]"
    >
      {children}
    </motion.div>
  );
};

/**
 * A robot act inside an existing section. Desktop: the section keeps the main
 * column and a sticky stage column beside it holds the cast + set piece, so the
 * android stays with you while you read that section. Phones: the stage is a
 * short band above the section. The wrapper is the fx3d [data-robot-stage].
 */
export function SectionAct({
  cast,
  prop,
  children,
  className = "",
}: {
  cast: CastMember[];
  prop?: string;
  children: React.ReactNode;
  className?: string;
}) {
  const pair = cast.length > 1;
  return (
    <div data-robot-stage="section" className={`relative flex flex-col lg:grid lg:grid-cols-[minmax(0,1fr)_28vw] ${className}`}>
      <div className="min-w-0 lg:order-1">{children}</div>
      <div aria-hidden className="relative order-first h-[36svh] border-cx-fg/5 lg:order-2 lg:h-auto lg:border-l">
        <div className="relative h-full lg:sticky lg:top-0 lg:h-[100svh]">
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_70%_55%_at_50%_62%,rgba(74,138,201,0.14),transparent_70%)]" />
          {prop && <PropSlot name={prop} className="left-[10%] right-[10%] top-[8%] h-[42%]" />}
          {pair ? (
            <>
              <ActorSlot {...cast[0]} className="bottom-[8%] left-[16%] h-[68%] w-[32%]" />
              <ActorSlot {...cast[1]} className="bottom-[8%] left-[52%] h-[65%] w-[32%]" />
            </>
          ) : (
            <ActorSlot {...cast[0]} className="bottom-[8%] left-[30%] h-[70%] w-[40%]" />
          )}
        </div>
      </div>
    </div>
  );
}

export const primaryBtn =
  "inline-flex items-center gap-2 rounded-full bg-[#2A629E] px-6 py-3.5 text-sm font-semibold text-white hover:bg-[#3A78BD] transition-colors";
export const ghostBtn =
  "inline-flex items-center gap-2 rounded-full border border-cx-fg/25 px-6 py-3.5 text-sm font-semibold text-cx-fg hover:border-cx-accent transition-colors";

export interface Cta {
  label: string;
  to: string;
}

const CtaLink = ({ cta, primary }: { cta: Cta; primary?: boolean }) => {
  const cls = primary ? primaryBtn : ghostBtn;
  const body = (
    <>
      {cta.label}
      {primary && <ArrowRight className="h-4 w-4" />}
    </>
  );
  return (
    <Magnetic>
      {cta.to.startsWith("#") || cta.to.startsWith("http") ? (
        <a href={cta.to} className={cls}>{body}</a>
      ) : (
        <Link to={cta.to} className={cls}>{body}</Link>
      )}
    </Magnetic>
  );
};

/** Full-screen player for a page's original film. */
export function FilmModal({
  src,
  open,
  onClose,
  title,
  onEnded,
}: {
  src: string;
  open: boolean;
  onClose: () => void;
  title: string;
  onEnded?: () => void;
}) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);
  // YouTube links (e.g. the Voice Agent demo) can't play in <video>; embed them
  const ytId = extractYouTubeId(src);
  const frame = "max-h-[86vh] w-full max-w-5xl rounded-2xl border border-white/10 bg-black shadow-2xl";
  return createPortal(
    <AnimatePresence>
      {open && (
        <motion.div
          role="dialog"
          aria-modal="true"
          aria-label={title}
          data-state="open"
          className="fixed inset-0 z-[80] flex items-center justify-center bg-[#030812]/90 p-4 backdrop-blur-sm"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
        >
          {ytId ? (
            <motion.iframe
              key={ytId}
              src={buildYouTubeEmbedUrl(ytId, { autoplay: true })}
              title={title}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              allowFullScreen
              referrerPolicy="strict-origin-when-cross-origin"
              className={`aspect-video ${frame}`}
              initial={{ scale: 0.94, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              transition={{ duration: 0.5, ease: easeOut }}
              onClick={(e) => e.stopPropagation()}
            />
          ) : (
            <motion.video
              src={src}
              controls
              autoPlay
              playsInline
              onEnded={onEnded}
              className={frame}
              initial={{ scale: 0.94, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              transition={{ duration: 0.5, ease: easeOut }}
              onClick={(e) => e.stopPropagation()}
            />
          )}
          <button
            type="button"
            onClick={onClose}
            aria-label="Close film"
            className="absolute right-5 top-5 flex h-11 w-11 items-center justify-center rounded-full border border-white/20 text-white hover:border-white/60"
          >
            <X className="h-5 w-5" />
          </button>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body,
  );
}

export interface CineHeroProps {
  eyebrow: string;
  title: React.ReactNode;
  lede?: React.ReactNode;
  /** Giant faint word behind the cast (defaults to none). */
  wordmark?: string;
  /** One or two androids. Two stand side by side; one stands centre-stage. */
  cast: CastMember[];
  /** three.js set piece behind the cast (fx3d/android/props). */
  prop?: string;
  primary?: Cta;
  secondary?: Cta;
  /** A page's original hero video, offered as "Watch the film". */
  film?: string;
  /** Shorter hero for utility pages (forms, legal, articles). */
  compact?: boolean;
  /** Label for the film button (default "Watch the film"). */
  filmLabel?: string;
  onFilmPlay?: () => void;
  /** Replaces the film modal, e.g. to start a video shown inline on the page. */
  onFilmClick?: () => void;
  onFilmEnded?: () => void;
  children?: React.ReactNode;
}

/**
 * The 3-D android hero: copy bottom-left, the cast + prop on the right
 * (stacked above the copy on phones). A [data-hero3d] stage for fx3d.
 */
export function CineHero({
  eyebrow,
  title,
  lede,
  wordmark,
  cast,
  prop = "halo",
  primary,
  secondary,
  film,
  compact,
  filmLabel = "Watch the film",
  onFilmPlay,
  onFilmClick,
  onFilmEnded,
  children,
}: CineHeroProps) {
  const ref = useRef<HTMLElement>(null);
  const [filmOpen, setFilmOpen] = useState(false);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const copyY = useTransform(scrollYProgress, [0, 1], [0, -100]);
  const copyO = useTransform(scrollYProgress, [0, 0.65], [1, 0]);
  const pair = cast.length > 1;
  // desktop slots (px from the right third); phones stack the stage above the copy
  const h = compact ? "md:h-[58vh]" : "md:h-[74vh]";
  const mh = compact ? "h-[24svh]" : "h-[28svh]";
  const mBottom = compact ? "bottom-[60%]" : "bottom-[60%]";

  return (
    <section
      ref={ref}
      data-hero3d
      className={`relative overflow-hidden bg-cx-bg text-cx-fg ${compact ? "min-h-[78svh] md:min-h-[72vh]" : "h-[100svh] min-h-[640px]"}`}
    >
      <div aria-hidden className="absolute inset-0 bg-[radial-gradient(ellipse_55%_55%_at_66%_68%,rgba(74,138,201,0.18),transparent_70%)]" />
      <div aria-hidden className="fx-grid-floor opacity-35" />
      {wordmark && (
        <motion.div
          aria-hidden
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1.5, ease: easeOut }}
          className="pointer-events-none absolute inset-x-0 top-[14%] select-none whitespace-nowrap text-center font-cine text-[20vw] font-bold leading-[0.8] tracking-[-0.07em] text-cx-fg/[0.05] md:top-[10%] md:text-[15vw]"
        >
          {wordmark}
        </motion.div>
      )}

      <PropSlot
        name={prop}
        className={`left-[6vw] top-[4%] w-[88vw] ${compact ? "h-[34svh]" : "h-[40svh]"} md:left-[48%] md:top-[12%] md:w-[38vw] ${compact ? "md:h-[56vh]" : "md:h-[70vh]"}`}
      />
      {pair ? (
        <>
          <ActorSlot {...cast[0]} scrub="turn" className={`${mBottom} left-[22%] ${mh} w-[28vw] md:bottom-[9%] md:left-[52%] ${h} md:w-[13vw]`} />
          <ActorSlot {...cast[1]} scrub="turn" className={`${mBottom} left-[50%] ${mh} w-[28vw] md:bottom-[9%] md:left-[67%] ${h} md:w-[13vw]`} />
        </>
      ) : (
        <ActorSlot {...cast[0]} scrub="turn" className={`${mBottom} left-[30%] ${mh} w-[40vw] md:bottom-[9%] md:left-[58%] ${h} md:w-[16vw]`} />
      )}

      <motion.div
        style={{ y: copyY, opacity: copyO }}
        className={`absolute inset-x-0 bottom-0 px-6 pb-36 md:left-[4%] md:right-auto md:max-w-[40vw] md:px-0 md:pb-0 ${compact ? "md:bottom-[12%]" : "md:bottom-[10%]"}`}
      >
        <motion.p
          initial={{ opacity: 0, letterSpacing: "0.5em" }}
          animate={{ opacity: 1, letterSpacing: "0.24em" }}
          transition={{ duration: 1.3, ease: easeOut, delay: 0.2 }}
          className="font-hud text-[11px] uppercase text-cx-link"
        >
          {eyebrow}
        </motion.p>
        <motion.h1
          initial={{ opacity: 0, y: 30, filter: "blur(10px)" }}
          animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
          transition={{ duration: 1, ease: easeOut, delay: 0.35 }}
          className={`mt-4 font-cine font-semibold leading-[0.95] tracking-[-0.045em] text-cx-fg text-balance ${
            compact ? "text-[clamp(2.2rem,4.6vw,4.2rem)]" : "text-[clamp(2.5rem,5.4vw,5.2rem)]"
          }`}
        >
          {title}
        </motion.h1>
        {lede && (
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: easeOut, delay: 0.55 }}
            className="mt-5 max-w-md text-[15px] leading-relaxed text-cx-fg/65 md:text-base"
          >
            {lede}
          </motion.p>
        )}
        {(primary || secondary || film || onFilmClick) && (
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: easeOut, delay: 0.7 }}
            className="mt-8 flex flex-wrap items-center gap-3"
          >
            {primary && <CtaLink cta={primary} primary />}
            {secondary && <CtaLink cta={secondary} />}
            {(film || onFilmClick) && (
              <button
                type="button"
                onClick={() => {
                  onFilmPlay?.();
                  if (onFilmClick) onFilmClick();
                  else setFilmOpen(true);
                }}
                aria-label={filmLabel}
                className="group inline-flex items-center gap-2 px-1 py-3.5 text-sm font-semibold text-cx-fg/80 hover:text-cx-fg"
              >
                <span className="flex h-11 w-11 items-center justify-center rounded-full border border-cx-fg/25 transition-colors group-hover:border-cx-accent sm:h-9 sm:w-9">
                  <Play className="ml-0.5 h-3.5 w-3.5 fill-current" />
                </span>
                {/* icon-only on phones so the CTA row stays clear of the chat button */}
                <span className="hidden sm:inline">{filmLabel}</span>
              </button>
            )}
          </motion.div>
        )}
        {children}
      </motion.div>

      {!compact && (
        <div aria-hidden className="absolute bottom-8 right-8 hidden items-center gap-3 font-hud text-[10px] uppercase tracking-[0.24em] text-cx-fg/50 md:flex">
          Scroll
          <span className="relative block h-12 w-px overflow-hidden bg-cx-fg/15">
            <span className="absolute inset-x-0 top-0 h-1/2 animate-[fx-scan_1.8s_ease-in-out_infinite] bg-[#2A629E]" />
          </span>
        </div>
      )}
      {film && <FilmModal src={film} open={filmOpen} onClose={() => setFilmOpen(false)} title={`${eyebrow} film`} onEnded={onFilmEnded} />}
    </section>
  );
}
